<!--
	00 / NAV — three floating glass islands: the mark, the map, the offer.

	The old bar sat IN the flow, so on the homepage it printed a 64px paper slab
	above the dark hero — a seam the design never wanted. This rail floats over
	the page instead (fixed, pointer-events off between the islands) and carries
	no full-width surface at all. Three capsules, each its own piece of glass.

	The glass reads the surface under it. Dark sections opt in with
	`data-tone="dark"` (Section already emits it; Hero's two dark bands declare it
	directly) and one IntersectionObserver watches a thin band at the top of the
	viewport — the rail's own footprint. When a dark section crosses it the whole
	rail re-tints: smoked glass and bone type over the product's surface, frosted
	paper and ink over the page. `tone` is the first-paint answer, for SSR and
	no-JS; the observer refines it.

	That same band answers the second question — which section you are actually
	in — so the links island lights the current one and a lozenge slides under it
	(it follows the pointer on hover, and returns to the current section on
	leave). No scroll listeners: rects are only read when a boundary crosses the
	rail. The page progress cut-line survives as a hairline along the rail's
	bottom edge, still fed by the shared scroll engine.
-->
<script lang="ts">
	import Logotype from '$lib/components/ui/Logotype.svelte';
	import { onMount, untrack } from 'svelte';
	import { resolve } from '$app/paths';
	import { scroll } from '$lib/motion/scroll.svelte';
	import { site, nav } from '$lib/content/site';

	type NavHref = (typeof nav.links)[number]['href'];

	let {
		/** The surface under the rail at first paint — SSR and no-JS only; the
		    observer takes over on mount. The homepage opens on the dark hero. */
		tone = 'paper'
	}: { tone?: 'paper' | 'dark' } = $props();

	/** The rail's own footprint in px — the band of viewport it sits in. */
	const BAND = 72;

	// Seeded once, on purpose: `tone` is the first-paint answer, and from mount
	// on the observer below is the only thing allowed to move this.
	let overDark = $state(untrack(() => tone === 'dark'));
	let ctaLit = $state(false);
	let activeHref = $state('');
	let hoverHref = $state('');

	let linksEl = $state<HTMLElement | null>(null);
	let lozX = $state(0);
	let lozW = $state(0);
	let lozOn = $state(false);

	/** Whole-page 0..1 progress from the shared engine — the rail's cut-line. */
	let pageId = $state('');
	const pageProgress = $derived(pageId ? scroll.progress(pageId) : 0);

	onMount(() => {
		const reg = scroll.registerPage();
		pageId = reg.id;
		return () => reg.dispose();
	});

	// Sanctioned $effect: IntersectionObserver + window subscriptions with cleanup
	// (CLAUDE.md "External subscriptions"). One effect owns all of them because
	// they answer one question — what is under the rail right now — and the answer
	// is read from rects whenever a section boundary crosses it.
	$effect(() => {
		if (typeof IntersectionObserver === 'undefined') return;

		const darks = Array.from(document.querySelectorAll<HTMLElement>('[data-tone="dark"]'));
		const targets: { href: NavHref; el: HTMLElement }[] = [];
		for (const link of nav.links) {
			const el = document.getElementById(link.href.split('#')[1] ?? '');
			if (el) targets.push({ href: link.href, el });
		}

		const read = () => {
			overDark = darks.some((el) => {
				const r = el.getBoundingClientRect();
				return r.top <= BAND && r.bottom > 0;
			});
			// Targets are in document order, so the last one whose top has passed
			// under the rail is the section you are reading.
			let current = '';
			for (const t of targets) {
				if (t.el.getBoundingClientRect().top <= BAND) current = t.href;
			}
			activeHref = current;
		};

		// The observation root is shrunk to the rail's own band, so the observer
		// fires exactly when the answer can change and never in between.
		let io: IntersectionObserver | null = null;
		const observe = () => {
			io?.disconnect();
			const shrink = Math.max(0, window.innerHeight - BAND);
			io = new IntersectionObserver(read, { rootMargin: `0px 0px -${shrink}px 0px` });
			for (const el of darks) io.observe(el);
			for (const t of targets) io.observe(t.el);
			read();
		};
		observe();

		window.addEventListener('resize', observe, { passive: true });
		// A hash jump can skip the band between frames; scrollend re-settles it.
		window.addEventListener('scrollend', read, { passive: true });

		// The offer island only fills with the accent once the hero's own CTA is
		// gone, so there is never more than one chartreuse fill in view.
		const heroCta = document.getElementById('hero-cta');
		const ctaIo = heroCta
			? new IntersectionObserver(([e]) => (ctaLit = !e.isIntersecting), { threshold: 0 })
			: null;
		if (heroCta && ctaIo) ctaIo.observe(heroCta);

		return () => {
			io?.disconnect();
			ctaIo?.disconnect();
			window.removeEventListener('resize', observe);
			window.removeEventListener('scrollend', read);
		};
	});

	// Sanctioned $effect: a DOM measurement (CLAUDE.md "DOM measurements"). The
	// lozenge is one element sliding between links, so it needs the target's box;
	// offsetLeft/offsetWidth are already relative to the padding box it is
	// positioned in, so no rect arithmetic is required.
	$effect(() => {
		const href = hoverHref || activeHref;
		const host = linksEl;
		if (!host) return;

		const place = () => {
			const el = href ? host.querySelector<HTMLElement>(`[data-href="${href}"]`) : null;
			if (!el) {
				lozOn = false;
				return;
			}
			lozX = el.offsetLeft;
			lozW = el.offsetWidth;
			lozOn = true;
		};
		place();

		// The row reflows when the viewport crosses into the compact link spacing
		// and when the display font swaps in — re-place the lozenge rather than
		// leave it stranded under the wrong label.
		const ro = new ResizeObserver(place);
		ro.observe(host);
		return () => ro.disconnect();
	});
</script>

<header class="rail" class:rail--dark={overDark}>
	<div class="rail__inner">
		<!-- 1 / the mark -->
		<a href={resolve('/')} class="glass brand" aria-label={site.name}>
			<Logotype class="brand__logo" />
			<span class="brand__desc">Pattern engineering</span>
		</a>

		<!-- 2 / the map -->
		<nav
			class="glass links"
			aria-label="Primary"
			bind:this={linksEl}
			onpointerleave={() => (hoverHref = '')}
			onfocusout={() => (hoverHref = '')}
		>
			<span
				class="links__lozenge"
				class:links__lozenge--on={lozOn}
				style:--loz-x="{lozX}px"
				style:--loz-w="{lozW}px"
				aria-hidden="true"
			></span>
			{#each nav.links as link (link.href)}
				<a
					href={resolve(link.href)}
					class="links__link"
					data-href={link.href}
					aria-current={activeHref === link.href ? 'true' : undefined}
					onpointerenter={() => (hoverHref = link.href)}
					onfocus={() => (hoverHref = link.href)}>{link.label}</a
				>
			{/each}
		</nav>

		<!-- 3 / the offer -->
		<a href={resolve(nav.cta.href)} class="glass cta" class:cta--lit={ctaLit}>
			{nav.cta.label}<span class="cta__offer">{nav.cta.offer}</span>
		</a>
	</div>

	<!-- Scroll progress as a cut line: the plotter traces the page as you read. -->
	<div class="rail__trace" style:transform="scaleX({pageProgress})" aria-hidden="true"></div>
</header>

<style>
	/* ── The rail ────────────────────────────────────────────────────────
	   No surface of its own: it is a frame for three pieces of glass, and it
	   lets the page through everywhere else. */
	.rail {
		position: fixed;
		inset: 0 0 auto 0;
		z-index: 50;
		padding-block: 0.6875rem;
		pointer-events: none;

		/* One tone switch. Every island reads these, and because the concrete
		   properties (background-color, color, box-shadow…) resolve through them,
		   a tone flip animates even though custom properties themselves don't. */
		--glass-bg: color-mix(in srgb, var(--color-surface) 70%, transparent);
		--glass-edge: color-mix(in srgb, var(--color-line-strong) 85%, transparent);
		--glass-sheen: rgba(255, 255, 255, 0.8);
		--glass-shadow: 0 1px 2px rgba(20, 20, 12, 0.05), 0 10px 28px -14px rgba(20, 20, 12, 0.3);
		--glass-ink: var(--color-ink-900);
		--glass-ink-dim: var(--color-ink-500);
		--glass-ink-faint: var(--color-ink-400);
		--glass-fill: color-mix(in srgb, var(--color-ink-900) 7%, transparent);
		--glass-rule: var(--color-line-strong);
		--glass-trace: color-mix(in srgb, var(--color-cut-600) 55%, transparent);
		--glass-scrim: var(--color-paper);
	}

	/* The page now runs UNDER the rail, so a line of body copy would otherwise be
	   sliced in half in the gaps between the islands. This strip blurs and washes
	   it out instead, and the mask ramps the whole effect to nothing well before
	   the rail ends — so there is no bar, no edge, and nothing at all to see when
	   there is nothing behind it. */
	.rail::before {
		content: '';
		position: absolute;
		inset: 0 0 auto 0;
		height: 5.5rem;
		z-index: -1;
		background-color: color-mix(in srgb, var(--glass-scrim) 68%, transparent);
		backdrop-filter: blur(12px);
		-webkit-backdrop-filter: blur(12px);
		mask-image: linear-gradient(180deg, #000 0%, #000 50%, transparent 100%);
		-webkit-mask-image: linear-gradient(180deg, #000 0%, #000 50%, transparent 100%);
		pointer-events: none;
		transition: background-color var(--dur-slow) var(--ease-out);
	}
	/* Smoked glass — the rail is over the product's own surface. */
	.rail--dark {
		--glass-bg: color-mix(in srgb, var(--color-app-900) 62%, transparent);
		--glass-edge: rgba(242, 241, 234, 0.14);
		--glass-sheen: rgba(255, 255, 255, 0.18);
		--glass-shadow: 0 1px 2px rgba(0, 0, 0, 0.3), 0 14px 34px -14px rgba(0, 0, 0, 0.7);
		--glass-ink: var(--color-bone-100);
		--glass-ink-dim: var(--color-bone-300);
		--glass-ink-faint: var(--color-bone-500);
		--glass-fill: rgba(242, 241, 234, 0.12);
		--glass-rule: rgba(242, 241, 234, 0.18);
		--glass-trace: color-mix(in srgb, var(--color-cut-400) 55%, transparent);
		--glass-scrim: var(--color-app-950);
	}
	/* Page ink would disappear against smoked glass — use the live accent. */
	.rail--dark :focus-visible {
		outline-color: var(--color-cut-400);
	}

	.rail__inner {
		display: grid;
		grid-template-columns: 1fr auto 1fr;
		align-items: center;
		gap: 0.625rem;
		width: 100%;
		max-width: var(--container-page);
		margin-inline: auto;
		padding-inline: var(--spacing-gutter);
	}

	/* The page's progress, drawn as a live cut line along the rail's edge. Kept to
	   a 1px hairline at half strength: any heavier and a rail with no bar behind
	   it grows a bar-shaped bottom border. The engine's damping is the easing —
	   no CSS transition, it would fight the rAF writes. At the top of the page it
	   has no width, so it isn't there at all. */
	.rail__trace {
		position: absolute;
		left: 0;
		bottom: 0;
		width: 100%;
		height: 1px;
		background-color: var(--glass-trace);
		transform: scaleX(0);
		transform-origin: left;
		pointer-events: none;
		transition: background-color var(--dur-slow) var(--ease-out);
	}

	/* ── The glass ───────────────────────────────────────────────────────
	   Translucent, blurred, with a specular rim along the top edge and light
	   pooling in the upper half. Press, don't float: nothing moves toward the
	   cursor, the capsule seats down instead. */
	.glass {
		position: relative;
		display: inline-flex;
		align-items: center;
		height: 2.75rem; /* 44px minimum hit target */
		border-radius: 999px;
		border: 1px solid var(--glass-edge);
		background-color: var(--glass-bg);
		color: var(--glass-ink);
		box-shadow:
			var(--glass-shadow),
			inset 0 1px 0 var(--glass-sheen);
		backdrop-filter: blur(18px) saturate(1.7);
		-webkit-backdrop-filter: blur(18px) saturate(1.7);
		pointer-events: auto;
		transition:
			background-color var(--dur-slow) var(--ease-out),
			border-color var(--dur-slow) var(--ease-out),
			box-shadow var(--dur-slow) var(--ease-out),
			color var(--dur-slow) var(--ease-out),
			transform var(--dur-fast) var(--ease-out);
	}
	.glass::before {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: inherit;
		background: linear-gradient(180deg, var(--glass-sheen), transparent 58%);
		opacity: 0.4;
		pointer-events: none;
	}
	a.glass:active {
		transform: translateY(1px);
	}
	/* Without a real backdrop filter the translucency just looks washed out. */
	@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
		.glass {
			--glass-bg: var(--color-surface);
		}
		.rail--dark .glass {
			--glass-bg: var(--color-app-900);
		}
	}
	@media (prefers-reduced-transparency: reduce) {
		.glass {
			--glass-bg: var(--color-surface);
			backdrop-filter: none;
			-webkit-backdrop-filter: none;
		}
		.rail--dark .glass {
			--glass-bg: var(--color-app-900);
		}
	}

	/* ── 1 / the mark ────────────────────────────────────────────────────── */
	/* Explicit columns: the map island is display:none on small screens, and
	   without them it would vacate column 2 and drag the offer off the edge. */
	.brand {
		grid-column: 1;
		justify-self: start;
		gap: 0.7rem;
		padding-inline: 0.85rem 1rem;
		--logo-accent: var(--color-cut-700);
	}
	.rail--dark .brand {
		--logo-accent: var(--color-cut-400);
	}
	.brand :global(.brand__logo) {
		height: 0.875rem;
	}
	.brand__desc {
		padding-left: 0.7rem;
		border-left: 1px solid var(--glass-rule);
		font-family: var(--font-mono);
		font-size: 0.625rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--glass-ink-faint);
		transition: color var(--dur-slow) var(--ease-out);
	}

	/* ── 2 / the map ─────────────────────────────────────────────────────── */
	.links {
		grid-column: 2;
		display: none;
		gap: 0.125rem;
		padding-inline: 0.3125rem;
	}
	@media (min-width: 660px) {
		.links {
			display: inline-flex;
		}
	}
	.links__link {
		position: relative;
		z-index: 1;
		display: inline-flex;
		align-items: center;
		height: 2rem;
		padding-inline: 0.8125rem;
		border-radius: 999px;
		font-size: 0.9375rem;
		font-weight: 500;
		letter-spacing: -0.01em;
		white-space: nowrap;
		color: var(--glass-ink-dim);
		transition: color var(--dur-med) var(--ease-out);
	}
	.links__link:hover,
	.links__link[aria-current] {
		color: var(--glass-ink);
	}
	/* One lozenge for the whole row, so the highlight travels between links
	   instead of blinking on and off under each. It follows the pointer, and
	   falls back to whichever section is currently under the rail. */
	.links__lozenge {
		position: absolute;
		top: 50%;
		left: 0;
		z-index: 0;
		height: 2rem;
		width: var(--loz-w);
		border-radius: 999px;
		background-color: var(--glass-fill);
		box-shadow: inset 0 1px 0 var(--glass-sheen);
		opacity: 0;
		pointer-events: none;
		transform: translate(var(--loz-x), -50%);
		transition:
			transform var(--dur-slow) var(--ease-out),
			width var(--dur-slow) var(--ease-out),
			background-color var(--dur-slow) var(--ease-out),
			box-shadow var(--dur-slow) var(--ease-out),
			opacity var(--dur-med) var(--ease-out);
	}
	.links__lozenge--on {
		opacity: 1;
	}

	/* ── 3 / the offer ───────────────────────────────────────────────────── */
	.cta {
		grid-column: 3;
		justify-self: end;
		gap: 0.5rem;
		padding-inline: 1.125rem;
		font-family: var(--font-sans);
		font-size: 0.9375rem;
		font-weight: 600;
		letter-spacing: -0.01em;
		white-space: nowrap;
	}
	.cta:hover {
		--glass-bg: color-mix(in srgb, var(--color-surface) 88%, transparent);
	}
	.rail--dark .cta:hover {
		--glass-bg: color-mix(in srgb, var(--color-app-800) 80%, transparent);
	}
	/* Once the hero's own CTA is off screen this capsule stops being glass and
	   becomes the one chartreuse fill on the page. */
	.cta--lit,
	.rail--dark .cta--lit {
		--glass-bg: var(--color-cut-400);
		--glass-edge: color-mix(in srgb, var(--color-cut-600) 55%, transparent);
		--glass-ink: var(--color-ink-900);
		--glass-sheen: rgba(255, 255, 255, 0.5);
		--glass-shadow: 0 1px 2px rgba(20, 20, 12, 0.1), 0 10px 26px -8px rgba(127, 174, 46, 0.55);
	}
	.cta--lit:hover,
	.rail--dark .cta--lit:hover {
		--glass-bg: #c4f56a; /* one step brighter than cut-400 — lights up */
	}
	/* "Save 55%" as a chip, not a second button: it inherits the capsule's ink,
	   so it reads on glass and on the accent fill without being restyled. */
	.cta__offer {
		padding: 0.15rem 0.45rem;
		border-radius: 999px;
		font-family: var(--font-mono);
		font-size: 0.625rem;
		font-weight: 500;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		background-color: color-mix(in srgb, currentColor 14%, transparent);
	}

	/* ── Narrowing ───────────────────────────────────────────────────────── */
	@media (max-width: 900px) {
		.brand__desc {
			display: none;
		}
		.links__link {
			padding-inline: 0.5rem;
			font-size: 0.875rem;
		}
	}
	/* The logotype runs wider than the old icon and name, so it steps down to leave
	   the offer its room on a phone. */
	@media (max-width: 480px) {
		.brand {
			padding-inline: 0.75rem 0.85rem;
		}
		.brand :global(.brand__logo) {
			height: 0.6875rem;
		}
	}
	/* The mark keeps its wordmark all the way down — only the offer chip, which
	   the pricing section repeats anyway, gives way on the narrowest phones. */
	@media (max-width: 359px) {
		.cta__offer {
			display: none;
		}
	}
</style>
